// CandidateInterview.js — Advanced Production Version
import React, { useState, useEffect, useRef, useCallback } from "react";
import { db, storage, auth } from "./firebase";
import { signOut } from "firebase/auth";
import ExcelAssessment from "./ExcelAssessment";
import * as XLSX from "xlsx";
import {
  fetchQuestions,
  fetchPositions,
  createLiveSession,
  updateLiveSession,
  saveInterview as saveToLark,
  updateInterview,
  discoverBase,
  discoverTables,
  createRecord,
  searchRecords,
  callLarkProxy,
  LARK_TABLES
} from "./larkService_v2";
import { useNavigate } from "react-router-dom";
import { uploadToCloudinary } from "./cloudinaryService";
import { Toaster, toast } from "react-hot-toast";
import ResumeUpload from "./ResumeUpload";
import EmployeeForm from "./EmployeeForm";
import { evaluateAnswer, translateToEnglish } from "./aiService";
import { useFaceDetection } from "./useFaceDetection";

function CandidateInterview({ user, role }) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [candidateName, setCandidateName] = useState(() => localStorage.getItem("iv_candidateName") || "");
  const [allQuestions, setAllQuestions] = useState([]);
  const [isFieldSelecting, setIsFieldSelecting] = useState(() => localStorage.getItem("iv_isFieldSelecting") === "true");
  const [selectedField, setSelectedField] = useState(() => localStorage.getItem("iv_selectedField") || "");

  // Position selection
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(() => {
    const saved = localStorage.getItem("iv_selectedPosition");
    return saved ? JSON.parse(saved) : null;
  });

  const [questions, setQuestions] = useState(() => {
    const saved = localStorage.getItem("iv_questions");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentIndex, setCurrentIndex] = useState(() => parseInt(localStorage.getItem("iv_currentIndex") || "0"));
  const [isInterviewStarted, setIsInterviewStarted] = useState(() => localStorage.getItem("iv_isInterviewStarted") === "true");
  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);

  const [resumeData, setResumeData] = useState(() => {
    const saved = localStorage.getItem("iv_resumeData");
    return saved ? JSON.parse(saved) : null;
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(() => localStorage.getItem("iv_isFormSubmitted") === "true");
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem("iv_answers");
    return saved ? JSON.parse(saved) : [];
  });
  const [currentInterviewId, setCurrentInterviewId] = useState(() => localStorage.getItem("iv_currentInterviewId") || null);

  const [isUploading, setIsUploading] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [stream, setStream] = useState(null);

  // Speech
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [translatedAnswer, setTranslatedAnswer] = useState("");
  const [spokenLanguage, setSpokenLanguage] = useState("en-US");
  const [isTranslating, setIsTranslating] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isMicOffline, setIsMicOffline] = useState(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");

  // Answers + scores
  const [interviewScore, setInterviewScore] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const [savedInterview, setSavedInterview] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [isSyncingVideo, setIsSyncingVideo] = useState(false);
  const [finalVideoBlob, setFinalVideoBlob] = useState(null); // Local backup
  const [isSyncError, setIsSyncError] = useState(false); 

  const currentInterviewIdRef = useRef(null);
  const [isExcelPhase, setIsExcelPhase] = useState(false);
  const [excelDataFinal, setExcelDataFinal] = useState(null);
  const [excelVideoBlob, setExcelVideoBlob] = useState(null);
  const [isEditingAnswer, setIsEditingAnswer] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState("");

  // Anti-cheating
  const [cheatingFlags, setCheatingFlags] = useState(0);
  const cheatingFlagsRef = useRef(0);
  const [tabWarning, setTabWarning] = useState(false);

  // Session tracking (for live monitor)
  const sessionDocRef = useRef(null);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const isListeningRef = useRef(false);

  // Refs for data integrity (avoid closure staleness)
  const answersRef = useRef([]);
  const resumeDataRef = useRef(null);
  const interviewScoreRef = useRef(0);
  const finalScoreRef = useRef(0);

  // Sync refs with state
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { resumeDataRef.current = resumeData; }, [resumeData]);
  useEffect(() => { interviewScoreRef.current = interviewScore; }, [interviewScore]);
  useEffect(() => { finalScoreRef.current = finalScore; }, [finalScore]);
  useEffect(() => { currentInterviewIdRef.current = currentInterviewId; }, [currentInterviewId]);

  // Face detection
  const { faceStatus, faceWarning } = useFaceDetection(videoRef, isInterviewStarted);
  const faceWarningShownRef = useRef(false);

  // ─── Persistence Logic ───
  useEffect(() => {
    localStorage.setItem("iv_candidateName", candidateName);
    localStorage.setItem("iv_isFieldSelecting", isFieldSelecting);
    localStorage.setItem("iv_selectedField", selectedField);
    localStorage.setItem("iv_questions", JSON.stringify(questions));
    localStorage.setItem("iv_currentIndex", currentIndex);
    localStorage.setItem("iv_isInterviewStarted", isInterviewStarted);
    localStorage.setItem("iv_resumeData", JSON.stringify(resumeData));
    localStorage.setItem("iv_isFormSubmitted", isFormSubmitted);
    localStorage.setItem("iv_answers", JSON.stringify(answers));
    if (currentInterviewId) localStorage.setItem("iv_currentInterviewId", currentInterviewId);
    if (selectedPosition) localStorage.setItem("iv_selectedPosition", JSON.stringify(selectedPosition));
  }, [candidateName, isFieldSelecting, selectedField, questions, currentIndex, isInterviewStarted, resumeData, isFormSubmitted, answers, currentInterviewId, selectedPosition]);

  // ─── Auto-select Field Logic ───
  useEffect(() => {
    if (selectedPosition?.title && allQuestions.length > 0 && !selectedField) {
      const title = selectedPosition.title.toLowerCase();
      const availableCategories = [...new Set(allQuestions.map(q => (q.category || q.Category || "").trim()).filter(Boolean))];
      
      let matched = "";
      // 1. Keyword mapping for common roles
      if (title.includes("it") || title.includes("developer") || title.includes("software") || title.includes("tech")) {
        matched = availableCategories.find(c => ["it", "information technology", "it developer", "tech"].includes(c.toLowerCase()));
      } else if (title.includes("civil")) {
        matched = availableCategories.find(c => c.toLowerCase().includes("civil"));
      } else if (title.includes("hr") || title.includes("human resource")) {
        matched = availableCategories.find(c => ["hr", "human resource", "human resources"].includes(c.toLowerCase()));
      } else if (title.includes("sales") || title.includes("marketing")) {
        matched = availableCategories.find(c => ["sales", "marketing"].includes(c.toLowerCase()));
      } else if (title.includes("account") || title.includes("finance")) {
        matched = availableCategories.find(c => ["accounts", "accounting", "finance"].includes(c.toLowerCase()));
      } else if (title.includes("operations") || title.includes("ops")) {
        matched = availableCategories.find(c => ["operations", "ops"].includes(c.toLowerCase()));
      }

      // 2. Fallback: Fuzzy match
      if (!matched) {
        matched = availableCategories.find(c => title.includes(c.toLowerCase()) || c.toLowerCase().includes(title));
      }

      if (matched) {
        console.log(`🎯 Auto-matched Field: ${matched}`);
        setSelectedField(matched);
        
        // Ensure technical questions are added if not already
        if (questions.length === 20) {
          const fieldQs = allQuestions.filter(q => (q.category || q.Category || "").toLowerCase() === matched.toLowerCase());
          if (fieldQs.length > 0) {
            const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
            const selected = shuffle(fieldQs).slice(0, 10);
            setQuestions(prev => [...prev, ...selected]);
          }
        }
      }
    }
  }, [selectedPosition, allQuestions, selectedField, questions.length]);

  // ─── Parallel Data Fetching ───
  useEffect(() => {
    // Hard timeout: never show loading screen for more than 4 seconds
    const safetyTimeout = setTimeout(() => {
      setIsInitializing(false);
      console.warn("⏱️ Initialization safety timeout reached (4s). Continuing without blocking.");
    }, 4000);

    const loadAllData = async () => {
      try {
        console.log("🔍 [DEBUG] Starting initialization...");
        const [qsDocs, posDocs] = await Promise.all([
          fetchQuestions(),
          fetchPositions()
        ]);

        console.log("🔍 [DEBUG] Data received:", { questions: qsDocs.length, positions: posDocs.length });
        
        setAllQuestions(qsDocs);
        setPositions(posDocs);

        // Select initial questions if not in localStorage
        if (localStorage.getItem("iv_questions") === null || JSON.parse(localStorage.getItem("iv_questions")).length === 0) {
          const general = qsDocs.filter(q => (q.Category || q.category || "").toLowerCase() === "general");
          const aptitude = qsDocs.filter(q => (q.Category || q.category || "").toLowerCase() === "aptitude");
          const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
          
          let selected = [];
          if (general.length > 0 || aptitude.length > 0) {
            selected = [
              ...shuffle(general).slice(0, 10),
              ...shuffle(aptitude).slice(0, 10)
            ];
          } else {
            selected = shuffle(qsDocs).slice(0, 20);
          }
          setQuestions(selected);
        }
      } catch (err) {
        console.error("Initialization error:", err);
        toast.error("Failed to load interview data.");
      } finally {
        clearTimeout(safetyTimeout);
        setIsInitializing(false);
      }
    };
    loadAllData();

    return () => clearTimeout(safetyTimeout);
  }, []);

  const navigate = useNavigate();

  // ─── Attach stream to video ───
  useEffect(() => {
    if (stream && videoRef.current) videoRef.current.srcObject = stream;
  }, [stream, isInterviewStarted]);

  // ─── Cleanup on unmount ───
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach(t => t.stop());
      recognitionRef.current?.stop();
      // Mark session as ended
      if (sessionDocRef.current) {
        updateLiveSession(sessionDocRef.current, { Status: "completed" });
      }
    };
  }, [stream]);

  // ─── Face warning toast (throttled) ───
  useEffect(() => {
    if (!isInterviewStarted) return;
    if (faceWarning && !faceWarningShownRef.current) {
      faceWarningShownRef.current = true;
      toast.error(faceWarning, { duration: 4000, icon: faceStatus === "multiple" ? "🚨" : "⚠️" });
      if (faceStatus === "multiple") {
        cheatingFlagsRef.current += 1;
        setCheatingFlags(cheatingFlagsRef.current);
      }
      setTimeout(() => { faceWarningShownRef.current = false; }, 5000);
    }
  }, [faceWarning, faceStatus, isInterviewStarted]);

  // ─── Tab switch detection ───
  useEffect(() => {
    if (!isInterviewStarted) return;
    const handleVisibility = () => {
      if (document.hidden) {
        cheatingFlagsRef.current += 1;
        setCheatingFlags(cheatingFlagsRef.current);
        setTabWarning(true);
        toast.error(`⚠️ Tab switch detected! (${cheatingFlagsRef.current} violation${cheatingFlagsRef.current > 1 ? "s" : ""})`, { duration: 5000 });
        setTimeout(() => setTabWarning(false), 4000);
      }
    };
    const handleUnload = () => {
      stream?.getTracks().forEach(t => t.stop());
      recognitionRef.current?.stop();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [isInterviewStarted, stream]);

  // ─── Guard: warn before leaving while the video is still uploading ───
  // The recording uploads in the background after the results screen shows.
  // If the candidate closes the tab mid-upload, the Video URL never reaches
  // the dashboard — so block the exit with a native warning until sync ends.
  useEffect(() => {
    if (!isSyncingVideo && !isSyncError) return;
    const warnUnsynced = (e) => {
      e.preventDefault();
      e.returnValue =
        "Your interview video is still uploading. If you leave now the recording will NOT be saved. Please wait for syncing to finish.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", warnUnsynced);
    return () => window.removeEventListener("beforeunload", warnUnsynced);
  }, [isSyncingVideo, isSyncError]);

  // ─── Speech Recognition ───
  const startListening = useCallback(() => {
    if (isMicOffline) {
      console.warn("🎙️ Speech Recognition skipped - Microphone is offline.");
      setIsListening(false);
      isListeningRef.current = false;
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Your browser doesn't support Speech Recognition. Please use Chrome.");
      return;
    }

    // Stop any existing recognition before starting a new one
    if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
    }

    finalTranscriptRef.current = "";
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = spokenLanguage;

    recognition.onresult = e => {
      if (!isListeningRef.current) return; // 🛑 Safety: ignore results if we've stopped
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalTranscriptRef.current += t + " ";
        else interim += t;
      }
      setCurrentAnswer(finalTranscriptRef.current + interim);
    };

    recognition.onerror = e => {
      console.error("Speech Recognition Error:", e.error);
      if (e.error === "not-allowed") {
        toast.error("Microphone blocked! Please ensure you are not in another call (like Lark/Zoom) and have allowed browser access.", { duration: 5000 });
        isListeningRef.current = false;
        setIsListening(false);
      } else if (e.error === "service-not-allowed") {
        toast.error("Speech service is disabled on this browser. Try using Chrome.");
      }
    };

    recognition.onend = () => {
      // Auto-restart if we're still supposed to be listening
      if (isListeningRef.current) {
        try { recognition.start(); } catch (e) {}
      } else {
        setIsListening(false);
      }
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      isListeningRef.current = true;
      setIsListening(true);
    } catch (e) {
      console.error("Speech start error:", e);
      setIsListening(false);
      isListeningRef.current = false;
    }
  }, [spokenLanguage]);

  const stopListening = useCallback(async (skipStateUpdate = false) => {
    isListeningRef.current = false;
    setIsListening(false);
    
    if (recognitionRef.current) {
      try {
        if (skipStateUpdate) {
          recognitionRef.current.abort(); // Immediately stop and discard
        } else {
          recognitionRef.current.stop(); // Gracefully stop
        }
      } catch(e) { console.warn("Mic stop error:", e); }
      recognitionRef.current = null;
    }
    
    // Auto-enable editing mode when listening stops
    const finalVal = (finalTranscriptRef.current || currentAnswer || "").trim();
    if (finalVal) {
      setEditedAnswer(finalVal);
    }

    if (skipStateUpdate) {
      finalTranscriptRef.current = "";
      setCurrentAnswer("");
      return;
    }

    const rawTranscript = (finalTranscriptRef.current || currentAnswer).trim();
    if (rawTranscript && spokenLanguage !== "en-US") {
      setIsTranslating(true);
      const toastId = toast.loading("🤖 Translating to English...");
      try {
        const translated = await translateToEnglish(rawTranscript);
        setTranslatedAnswer(prev => prev ? prev + " " + translated : translated);
        toast.success("Translation complete!", { id: toastId });
      } catch (err) {
        console.error("Translation error:", err);
        toast.error("Translation failed. Using original text.", { id: toastId });
        setTranslatedAnswer(prev => prev ? prev + " " + rawTranscript : rawTranscript);
      } finally {
        setIsTranslating(false);
      }
    } else if (rawTranscript) {
      setTranslatedAnswer(prev => prev ? prev + " " + rawTranscript : rawTranscript);
    }
    
    // Clear local buffers after saving
    finalTranscriptRef.current = "";
    setCurrentAnswer("");
  }, [spokenLanguage, currentAnswer]);

  // ─── Update live session doc ───
  const updateSession = async (data) => {
    if (!sessionDocRef.current) return;
    // Map fields to Lark names
    const mappedData = {};
    if (data.currentQuestion !== undefined) mappedData["Current Question"] = data.currentQuestion;
    if (data.cheatingFlags !== undefined) mappedData["Cheating Flags"] = data.cheatingFlags;
    if (data.status !== undefined) mappedData["Status"] = data.status;

    try { await updateLiveSession(sessionDocRef.current, mappedData); } catch (_) {}
  };

  // ─── Start Interview ───
  const handleStartInterview = async () => {
    if (!resumeData) {
      toast.error("Please upload your resume first.");
      return;
    }
    try {
      // 🎯 Lower resolution = smaller file size (640x480 is plenty for interviews)
      let mediaStream;
      try {
        // 🎯 Attempt 1: Optimized settings for low bandwidth
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 480 }, height: { ideal: 360 }, frameRate: { ideal: 15, max: 20 } },
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 22050 }
        });
      } catch (firstErr) {
        console.warn("⚠️ Optimized media request failed, trying simple fallback:", firstErr);
        try {
          // 🎯 Attempt 2: Simple fallback (most compatible)
          mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch (secondErr) {
          console.warn("⚠️ Simple fallback failed, trying Video ONLY:", secondErr);
          try {
            // 🎯 Attempt 3: Video ONLY (if mic is broken)
            mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
            setIsMicOffline(true);
            toast.error("🎤 Microphone Blocked! You will need to TYPE your answers manually.", { duration: 8000 });
          } catch (thirdErr) {
            console.warn("⚠️ Video ONLY failed, trying Audio ONLY:", thirdErr);
            // 🎯 Attempt 4: Audio ONLY (if camera is broken)
            mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            toast.error("📷 Camera failed! Interview will proceed with AUDIO ONLY.");
          }
        }
      }

      // Check if mic is actually missing/blocked after all attempts
      const hasAudio = mediaStream.getAudioTracks().length > 0;
      if (!hasAudio) {
        setIsMicOffline(true);
      }
      
      // 🚀 Robust MIME Check: Support Chrome (WebM) and iOS/Safari (MP4)
      let mimeType = "video/webm;codecs=vp8,opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/mp4"; // iOS/Safari Fallback
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/quicktime"; // Final fallback
        }
      }
      console.log(`🎬 Recording started with MIME type: ${mimeType}`);
      
      // 📦 Ultra-low bitrate for instant syncing
      const options = { mimeType, videoBitsPerSecond: 100000, audioBitsPerSecond: 32000 };
      const recorder = new MediaRecorder(mediaStream, options);
      recordedChunksRef.current = [];
      recorder.ondataavailable = e => { 
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data); 
          console.log(`Video chunk received: ${e.data.size} bytes. Total chunks: ${recordedChunksRef.current.length}`);
        }
      };
      recorder.onstop = async () => await handleSaveInterview();
      recorder.start(1000); // 🚀 Capture data every 1 second to ensure non-empty blobs
      mediaRecorderRef.current = recorder;
      setStream(mediaStream);
      setIsInterviewStarted(true);
      setAnswers([]);

      // Update the primary interview record to "active" status for the main dashboard
      if (currentInterviewIdRef.current) {
        await updateInterview(currentInterviewIdRef.current, { 
          "Status": "active",
          "Started At": Date.now() 
        });
        console.log("Interview record updated to active:", currentInterviewIdRef.current);
      }

      // Create live session record in Lark
      const userId = user?.uid || user?.email || "anonymous";
      const recordId = await createLiveSession({
        userId,
        candidateName: candidateName || "Anonymous",
        status: "active",
        currentQuestion: 0,
        totalQuestions: questions.length,
        cheatingFlags: 0,
      });
      sessionDocRef.current = recordId;

      toast.success("🎬 Interview started! Recording has begun.", { duration: 3000 });
      
      // Initial auto-select attempt (mostly handled by useEffect now)
      if (selectedPosition?.title && allQuestions.length > 0 && !selectedField) {
         // (Rest of the logic is in the useEffect above)
      }

      setTimeout(() => startListening(), 600);
    } catch (err) {
      console.error("Camera/Mic Error:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        toast.error("🔒 Access Denied! Please click the 'Lock' icon in the address bar and set Camera & Mic to 'Allow', then refresh.", { duration: 6000 });
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        toast.error("🔌 Camera in Use! Please close other apps (Zoom, Teams, etc.) or other browser tabs that might be using your camera.", { duration: 6000 });
      } else {
        toast.error("⚠️ Hardware Error: Could not connect to camera/mic. Please check your connections or try a different browser.");
      }
    }
  };

  // ─── Next Question / Submit ───
  const handleNextQuestion = useCallback(async () => {
    if (!questions[currentIndex]) {
      console.error("❌ [CRITICAL] Question at index", currentIndex, "is undefined. Questions length:", questions.length);
      toast.error("Technical error: Question missing. Please refresh.");
      return;
    }

    const questionText = questions[currentIndex].question || questions[currentIndex].Question;
    // Prioritize the editable field content
    const answerText = (editedAnswer || translatedAnswer || currentAnswer || "").trim();
    
    // Evaluate answer instantly or treat as skipped if empty
    let evalResult;
    if (!answerText) {
      evalResult = { score: 3, feedback: "Candidate did not provide an answer. Awarded base score of 3." };
    } else {
      setIsEvaluating(true);
      const evalToast = toast.loading("🤖 AI evaluating your answer...");
      evalResult = await evaluateAnswer(
        questionText, 
        answerText, 
        selectedPosition?.jobDescription || "",
        questions[currentIndex].category || questions[currentIndex].Category || ""
      );
      setIsEvaluating(false);
      toast.dismiss(evalToast);
    }

    const newAnswer = {
      question: questionText,
      answer: answerText || "(No answer provided)",
      score: evalResult.score,
      feedback: evalResult.feedback,
    };

    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);
    
    // 🎙️ STEP 2: Stop and Clear mic before next question
    await stopListening(true);
    setCurrentAnswer("");
    setTranslatedAnswer("");
    setEditedAnswer("");
    setIsEditingAnswer(false);
    finalTranscriptRef.current = "";

    const nextIndex = currentIndex + 1;

    // Update live session
    await updateSession({
      currentQuestion: nextIndex,
      cheatingFlags: cheatingFlagsRef.current,
    });

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
      setTimeout(() => { startListening(); }, 600); // Increased delay for stability
      toast(`Question ${nextIndex + 1} of ${questions.length}`, { icon: "📋", duration: 2000 });
    } else if (nextIndex === 20 && !selectedField) {
      // Pause for field selection after General + Aptitude ONLY if not auto-selected
      setIsFieldSelecting(true);
      await stopListening();
      toast("Great job! Now, select your specialized field.", { icon: "🎯", duration: 3000 });
    } else if (nextIndex === 20 && selectedField) {
      // Auto-selected field already present, verify we have questions
      if (questions.length > 20) {
        setCurrentIndex(nextIndex);
        setTimeout(() => { startListening(); }, 600);
        toast(`Technical Round: ${selectedField}`, { icon: "💻", duration: 3000 });
      } else {
        // Fallback: if pre-fetch failed, try one last time
        console.warn("⚠️ Questions length was 20 despite selectedField. Fetching now...");
        const fieldQuestions = allQuestions.filter(q => (q.category || q.Category || "").toLowerCase() === selectedField.toLowerCase());
        if (fieldQuestions.length > 0) {
           const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
           const selectedFieldQuestions = shuffle(fieldQuestions).slice(0, 10);
           setQuestions(prev => [...prev, ...selectedFieldQuestions]);
           setCurrentIndex(nextIndex);
           setTimeout(() => { startListening(); }, 600);
           toast(`Technical Round: ${selectedField}`, { icon: "💻", duration: 3000 });
        } else {
           toast.error("No technical questions found for your field. Completing interview...");
           handleEndInterview(updatedAnswers);
        }
      }
    } else {
      handleEndInterview(updatedAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentAnswer, translatedAnswer, editedAnswer, isEditingAnswer, answers, questions, stopListening, startListening, selectedField]);

  // ─── Skip Question ───
  const handleSkipQuestion = useCallback(async () => {
    const questionText = questions[currentIndex].question || questions[currentIndex].Question;
    const answerText = "Question skipped by candidate";
    
    const newAnswer = {
      question: questionText,
      answer: answerText,
      score: 3,
      feedback: "Question skipped by candidate. Awarded base score of 3.",
    };

    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);
    
    // 🎙️ STEP 2: Stop and Clear mic before next question
    await stopListening(true);
    setCurrentAnswer("");
    setTranslatedAnswer("");
    finalTranscriptRef.current = "";

    const nextIndex = currentIndex + 1;

    // Update live session
    await updateSession({
      currentQuestion: nextIndex,
      cheatingFlags: cheatingFlagsRef.current,
    });

    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
      setTimeout(() => { startListening(); }, 600); // Increased delay for stability
      toast(`Question ${nextIndex + 1} of ${questions.length}`, { icon: "⏭️", duration: 2000 });
    } else if (nextIndex === 20 && !selectedField) {
      // Pause for field selection after General + Aptitude ONLY if not auto-selected
      setIsFieldSelecting(true);
      await stopListening();
      toast("Aptitude phase complete. Select your specialized field.", { icon: "🎯", duration: 3000 });
    } else if (nextIndex === 20 && selectedField) {
      // Auto-selected field already present, verify we have questions
      if (questions.length > 20) {
        setCurrentIndex(nextIndex);
        setTimeout(() => { startListening(); }, 600);
        toast(`Technical Round: ${selectedField}`, { icon: "💻", duration: 3000 });
      } else {
        // Fallback: if pre-fetch failed, try one last time
        const fieldQuestions = allQuestions.filter(q => (q.category || q.Category || "").toLowerCase() === selectedField.toLowerCase());
        if (fieldQuestions.length > 0) {
           const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
           const selectedFieldQuestions = shuffle(fieldQuestions).slice(0, 10);
           setQuestions(prev => [...prev, ...selectedFieldQuestions]);
           setCurrentIndex(nextIndex);
           setTimeout(() => { startListening(); }, 600);
        } else {
           handleEndInterview(updatedAnswers);
        }
      }
    } else {
      handleEndInterview(updatedAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, answers, questions, stopListening, startListening, selectedField]);

  // ─── End Interview ───
  const handleEndInterview = async (finalAnswers) => {
    await stopListening(true);

    // Stop the recorder and WAIT for it to fully flush the final chunk.
    // MediaRecorder.stop() delivers the last dataavailable event ASYNCHRONOUSLY,
    // so we must await the onstop event before building the blob — otherwise the
    // recording can be truncated (or empty on short interviews), which silently
    // skips the upload and leaves "Video Syncing..." forever in the dashboard.
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      await new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        recorder.onstop = finish;
        try {
          recorder.requestData(); // flush any buffered data first
        } catch (_) {}
        try { recorder.stop(); } catch (_) { finish(); }
        // Safety: never hang the completion flow if onstop doesn't fire
        setTimeout(finish, 3000);
      });
    }

    console.log(`Q&A Finished. Final blob: ${recordedChunksRef.current.length} chunks. Uploading interview results.`);

    // Save interview data exactly ONCE
    await handleSaveInterview(finalAnswers);

    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
  };

  // Called manually from Results Screen when candidate clicks "Take Optional Excel Test"
  const handleOptionalExcelSubmit = async (sheetData, screenBlob) => {
    // 🚀 Keep overlay open until upload finishes to prevent premature closure
    toast.loading("📤 Submitting your Excel data...", { id: "excel-submit" });
    
    setExcelDataFinal(sheetData);
    setExcelVideoBlob(screenBlob);

    try {
      let finalDocId = currentInterviewIdRef.current || currentInterviewId || localStorage.getItem("iv_currentInterviewId");
      
      // 🕵️ EMERGENCY FALLBACK: Search Lark if ID is still missing
      if (!finalDocId) {
        console.log("🕵️ ID missing from state, searching Lark by User ID...");
        const userId = user?.uid || user?.email || "anonymous";
        try {
          // Attempt 1: Search API
          let existingRecords = await searchRecords(LARK_TABLES.INTERVIEWS, `CurrentValue.[User ID] == "${userId}"`);
          
          // Attempt 2: Manual Filter (if search returns nothing)
          if (!existingRecords || existingRecords.length === 0) {
            console.log("⚠️ Search API returned nothing, fetching all records for manual filter...");
            const allRes = await callLarkProxy("GET", `/tables/${LARK_TABLES.INTERVIEWS}/records?page_size=500`);
            if (allRes.data?.items) {
              existingRecords = allRes.data.items
                .filter(item => (item.fields["User ID"] || item.fields["userId"]) === userId)
                .map(item => ({ id: item.record_id, ...item.fields }));
            }
          }

          if (existingRecords && existingRecords.length > 0) {
            // Get the latest one
            const latest = existingRecords.sort((a, b) => {
              const timeA = a.fields?.["Completed At"] || 0;
              const timeB = b.fields?.["Completed At"] || 0;
              return timeB - timeA;
            })[0];
            finalDocId = latest.id;
            console.log("✅ Recovered ID from Lark manually:", finalDocId);
            setCurrentInterviewId(finalDocId);
            currentInterviewIdRef.current = finalDocId;
          }
        } catch (searchErr) {
          console.error("❌ ID recovery search failed:", searchErr);
        }
      }

      if (!finalDocId) {
        toast.error("Error: Could not find interview record. Please refresh or contact support.", { id: "excel-submit" });
        setIsExcelPhase(false);
        return;
      }

      // 1. Instantly save the raw JSON Excel Data to Lark
      try {
        console.log("💾 Saving Excel submission logs to record:", finalDocId);
        
        // 🕵️ DEBUG: Log the recording status
        const logs = {
          submissionType: "Screen Recording Only",
          timestamp: new Date().toISOString(),
          blobSize: screenBlob?.size || 0,
          blobType: screenBlob?.type || "unknown",
          docId: finalDocId
        };
        
        await updateInterview(finalDocId, { "Excel Data": JSON.stringify(logs) });
        console.log("✅ Excel logs saved.");
      } catch (jsonErr) {
        console.error("❌ Failed to save Excel logs:", jsonErr);
      }

      // 2. Background sync the screen recording
      if (screenBlob && screenBlob.size > 100) {
        console.log(`🎥 Uploading Screen Recording (${Math.round(screenBlob.size/1024)} KB)...`);
        const userId = user?.uid || user?.email || "anonymous";
        const folderName = `interviews/${userId}_excel`;
        
        try {
          const screenDownloadUrl = await uploadToCloudinary(screenBlob, folderName, (prog) => {
            toast.loading(`🎥 Uploading Screen Recording: ${prog}%`, { id: "excel-submit" });
          }, "video");

          if (screenDownloadUrl) {
            console.log("🔗 Screen Recording URL:", screenDownloadUrl);
            // Save Excel Video URL
            try {
              await updateInterview(finalDocId, { "Excel Video": screenDownloadUrl });
              console.log("✅ Excel Video field updated in Lark.");
            } catch (urlErr) {
              console.error("❌ Failed to save to 'Excel Video' field, trying 'Excel Video URL':", urlErr);
              try {
                await updateInterview(finalDocId, { "Excel Video URL": screenDownloadUrl });
                console.log("✅ Excel Video URL field updated in Lark.");
              } catch (_) {
                console.warn("⚠️ Could not save Excel Video under any known field name.");
              }
            }
          }
        } catch (videoErr) {
          console.error("❌ Failed to upload Excel video:", videoErr);
          toast.error("Video upload failed, but data was saved.", { id: "excel-submit" });
        }
      } else {
        console.warn("⚠️ Screen recording blob is empty or too small.");
      }

      toast.success("✅ Excel Assessment completed and saved!", { id: "excel-submit" });
    } catch (e) {
      console.error("Excel save error:", e);
      toast.error("Failed to save Excel Assessment.", { id: "excel-submit" });
    } finally {
      // 🚀 Finally close the overlay
      setIsExcelPhase(false);
    }
  };

  const handleFieldSelect = (field) => {
    setSelectedField(field);
    const fieldQuestions = allQuestions.filter(q => (q.category || "").toLowerCase() === field.toLowerCase());
    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    const selectedFieldQuestions = shuffle(fieldQuestions).slice(0, 10);
    
    setQuestions(prev => [...prev, ...selectedFieldQuestions]);
    setIsFieldSelecting(false);
    setCurrentAnswer("");
    setTranslatedAnswer("");
    setEditedAnswer("");
    setIsEditingAnswer(false);
    finalTranscriptRef.current = "";
    
    toast(`Field set to ${field}. Here are your technical questions.`, { icon: "💻" });
    setTimeout(() => {
        setCurrentIndex(20);
        startListening();
    }, 1000);
  };

  const handleSaveInterview = async (passedAnswers = null) => {
    // 🎙️ Get latest data from argument OR refs
    const finalAnswers = passedAnswers || answersRef.current || [];
    const finalResumeData = resumeDataRef.current;
    const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
    const userId = user?.uid || user?.email || "anonymous";

    setIsUploading(true);
    setIsSyncingVideo(true);

    try {
      // 🚀 STEP 0: Calculate Final Scores
      let interviewScoreVal = 0;
      if (finalAnswers.length > 0) {
        const sum = finalAnswers.reduce((acc, curr) => acc + (Number(curr.score) || 0), 0);
        interviewScoreVal = Math.round((sum / finalAnswers.length) * 10) / 10;
      }
      const resScore = finalResumeData?.resumeScore || resumeData?.resumeScore || 0;
      const finalScoreVal = Math.round(((resScore * 0.4) + (interviewScoreVal * 0.6)) * 10) / 10;

      // Update state for UI
      setInterviewScore(interviewScoreVal);
      setFinalScore(finalScoreVal);

      // 🚀 STEP 1: Instant Firestore Data Save (Answers + Scores)
      const interviewData = {
        userId,
        candidateName: candidateName || "Anonymous",
        positionTitle: selectedPosition?.title || "",
        positionJobDescription: selectedPosition?.jobDescription || "",
        totalQuestions: questions.length,
        completedAt: Date.now(),
        resumeUrl: finalResumeData?.resumeUrl || resumeData?.resumeUrl || "",
        resumeFileName: finalResumeData?.resumeFileName || resumeData?.resumeFileName || "",
        resumeScore: resScore,
        resumeFeedback: finalResumeData?.resumeFeedback || resumeData?.resumeFeedback || "N/A",
        resumeDetails: finalResumeData?.resumeDetails || resumeData?.resumeDetails || {},
        resumeStrengths: finalResumeData?.strengths || resumeData?.strengths || [],
        resumeWeaknesses: finalResumeData?.weaknesses || resumeData?.weaknesses || [],
        answers: finalAnswers,
        interviewScore: interviewScoreVal,
        finalScore: finalScoreVal,
        cheatingFlags: cheatingFlagsRef.current,
        status: "completed",
        videoSynced: false,
      };

      let finalDocId = currentInterviewIdRef.current;
      if (finalDocId) {
        await updateInterview(finalDocId, {
          "Candidate Name": candidateName || "Anonymous",
          "Position Title": selectedPosition?.title || "",
          "Interview Score": interviewScoreVal,
          "Final Score": finalScoreVal,
          "Answers JSON": JSON.stringify(finalAnswers),
          "Resume Score": resScore || 0,
          "Resume URL": finalResumeData?.resumeUrl || resumeData?.resumeUrl || "",
          "Status": "completed",
          "Completed At": Date.now(),
          "Cheating Flags": cheatingFlagsRef.current
        });
      } else {
        finalDocId = await saveToLark(interviewData);
        setCurrentInterviewId(finalDocId);
        currentInterviewIdRef.current = finalDocId;
      }
      
      setSavedInterview({ id: finalDocId, ...interviewData });
      setIsInterviewCompleted(true);
      await updateSession({ status: "completed", completedAt: Date.now() });
      
      // 🚀 STEP 2: Transition UI immediately!
      setIsUploading(false); // Stop the spinner, the Results screen will show now
      toast.dismiss("submit"); // Clear the persistent loader
      toast.success("✅ Dashboard updated with your results!");

      setFinalVideoBlob(blob); // Save local backup for manual download if cloud fails
      setIsSyncError(false); 

      // 🚀 STEP 3: Background Video Upload (Progressive)
      if (blob.size > 100) { 
        setIsSyncingVideo(true);
        try {
          const folderName = `interviews/${userId}`;

          // 🔁 Retry the upload up to 3 times so a transient network blip
          //    doesn't permanently lose the recording.
          let downloadUrl = null;
          let lastErr = null;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              downloadUrl = await uploadToCloudinary(blob, folderName, (prog) => {
                setSyncProgress(prog);
              }, "video");
              break;
            } catch (upErr) {
              lastErr = upErr;
              console.warn(`⚠️ Video upload attempt ${attempt}/3 failed:`, upErr.message);
              setSyncProgress(0);
              if (attempt < 3) await new Promise((r) => setTimeout(r, 2000 * attempt));
            }
          }
          if (!downloadUrl) throw lastErr || new Error("Upload failed after 3 attempts");

          // Save Video URL first (critical) — with its own retry, since the
          // upload can succeed but the Lark PATCH fail, leaving "Video Syncing..."
          let urlSaved = false;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              await updateInterview(finalDocId, { "Video URL": downloadUrl });
              console.log("✅ Video URL saved to Lark:", downloadUrl);
              urlSaved = true;
              break;
            } catch (urlErr) {
              console.error(`❌ Attempt ${attempt}/3 to save Video URL failed:`, urlErr.message);
              if (attempt < 3) await new Promise((r) => setTimeout(r, 2000 * attempt));
            }
          }
          if (!urlSaved) throw new Error("Video uploaded but could not be saved to the dashboard.");

          // Attempt to set Video Synced flag (non-critical, field may not exist)
          try {
            await updateInterview(finalDocId, { "Video Synced": true });
          } catch (_) {
            console.warn("⚠️ 'Video Synced' field may not exist in Lark table — skipping.");
          }
          
          const keys = ["iv_candidateName", "iv_isFieldSelecting", "iv_selectedField", "iv_questions", "iv_currentIndex", "iv_isInterviewStarted", "iv_resumeData", "iv_isFormSubmitted", "iv_answers", "iv_selectedPosition"];
          keys.forEach(k => localStorage.removeItem(k));

          setIsSyncingVideo(false);
          setSyncProgress(100);
          toast.success("🎥 Interview files synced to cloud!", { duration: 3000 });
        } catch (error) {
          console.error("Background video sync failed!", error);
          setIsSyncingVideo(false);
          setIsSyncError(true);
          toast.error(error.message || "Cloud sync interrupted. You can retry or download manually below.", { duration: 5000 });
        }
      } else {
        console.warn(`⚠️ Video blob too small to sync (${blob.size} bytes, ${recordedChunksRef.current.length} chunks). Recording likely failed.`);
        setIsSyncingVideo(false);
        toast.error("⚠️ No video was recorded for this interview (camera may have been blocked).", { duration: 6000 });
      }

    } catch (err) {
      console.error("Final save error:", err);
      toast.error("Failed to save final results. Please try again.", { id: "submit" });
      setIsUploading(false);
    }
  };

  const getScoreColor = (s) => s >= 7 ? "#16a34a" : s >= 4 ? "#d97706" : "#dc2626";

  // ─── Loading ───
  if (isInitializing) {
    return (
      <div className="interview-page" style={{ 
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", 
        minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "white" 
      }}>
        <div style={{ 
          width: "48px", height: "48px", border: "4px solid rgba(99,102,241,0.3)", 
          borderTopColor: "#6366f1", borderRadius: "50%", animation: "spinner 0.8s linear infinite" 
        }}></div>
        <h2 style={{ marginTop: "20px", fontSize: "1.3rem", fontWeight: 600, letterSpacing: "0.5px" }}>
          {isUploading ? "Submitting Interview..." : "Setting up your interview..."}
        </h2>
        <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginTop: "6px" }}>
          Loading questions and positions
        </p>
      </div>
    );
  }

  // ─── If No Questions Available ───
  if (!isInitializing && questions.length === 0 && !isInterviewStarted && selectedPosition) {
     return (
       <div className="interview-page loading-screen dark-theme">
         <div style={{ textAlign: "center", padding: "40px" }}>
           <h2>⚠️ No Questions Found</h2>
           <p style={{ color: "#94a3b8" }}>There are no interview questions available for this position in the database. Please contact the administrator.</p>
           <button onClick={() => { setSelectedPosition(null); localStorage.removeItem("iv_selectedPosition"); }} style={{ marginTop: "20px", padding: "10px 20px", borderRadius: "8px", background: "#3b82f6", color: "white", border: "none", cursor: "pointer" }}>Go Back</button>
         </div>
       </div>
     )
  }

  // ─── Results ───
  if (isInterviewCompleted && savedInterview) {
    return (
      <div className="interview-page">
        <Toaster position="top-right" />

        {/* 🔒 Blocking video-sync overlay — covers the whole screen (incl. Log Out)
            so the candidate cannot leave before the recording finishes uploading.
            This is what guarantees the video reaches the dashboard. */}
        {(isSyncingVideo || isSyncError) && !isExcelPhase && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 100000,
            background: "rgba(15, 23, 42, 0.92)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20
          }}>
            <div style={{
              background: "white", borderRadius: 20, padding: "36px 32px", maxWidth: 440, width: "100%",
              textAlign: "center", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)"
            }}>
              {!isSyncError ? (
                <>
                  <div style={{
                    width: 54, height: 54, margin: "0 auto 20px", borderRadius: "50%",
                    border: "5px solid #e0e7ff", borderTopColor: "#4f46e5",
                    animation: "spinner 0.9s linear infinite"
                  }} />
                  <h2 style={{ fontSize: 22, color: "#1e293b", margin: "0 0 8px" }}>Saving your interview…</h2>
                  <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 18px", lineHeight: 1.5 }}>
                    Your video recording is uploading. <strong>Please do NOT close or leave this page</strong> —
                    it will finish in a few moments.
                  </p>
                  <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${syncProgress}%`, height: "100%", background: "#4f46e5", transition: "width 0.3s ease" }} />
                  </div>
                  <p style={{ color: "#4f46e5", fontSize: 13, fontWeight: 600, marginTop: 10 }}>{syncProgress}%</p>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
                  <h2 style={{ fontSize: 20, color: "#1e293b", margin: "0 0 8px" }}>Upload interrupted</h2>
                  <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
                    Your video didn't finish uploading. Please retry — don't leave the page until it succeeds.
                  </p>
                  <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                      onClick={() => handleSaveInterview()}
                      style={{ background: "#4f46e5", color: "white", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                    >🔄 Retry Upload</button>
                    {finalVideoBlob && (
                      <button
                        onClick={() => {
                          const url = URL.createObjectURL(finalVideoBlob);
                          const a = document.createElement("a");
                          a.href = url; a.download = `interview_${Date.now()}.webm`; a.click();
                        }}
                        style={{ background: "#10b981", color: "white", border: "none", padding: "10px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                      >⬇️ Download Backup</button>
                    )}
                  </div>
                  <button
                    onClick={() => { setIsSyncError(false); setIsSyncingVideo(false); }}
                    style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 12, marginTop: 16, cursor: "pointer", textDecoration: "underline" }}
                  >Skip and continue anyway</button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Excel Assessment Phase Overlay */}
        {isExcelPhase && (
           <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 99999, backgroundColor: "#0f172a" }}>
             <ExcelAssessment onComplete={handleOptionalExcelSubmit} />
           </div>
        )}

        <div className="results-container">
          <div className="results-header">
            <div className="results-check">✓</div>
            <h2>Interview Complete!</h2>
            <p>Your evaluation report is ready</p>
            {isSyncingVideo && (
              <div className="sync-status" style={{ 
                margin: "12px 0", 
                padding: "8px 16px", 
                background: "#f0f9ff", 
                border: "1px solid #bae6fd", 
                borderRadius: 8,
                fontSize: 13,
                color: "#0369a1",
                display: "flex",
                alignItems: "center",
                gap: 10,
                justifyContent: "center"
              }}>
                <span className="sync-spinner" style={{ 
                  width: 14, 
                  height: 14, 
                  border: "2px solid #0369a1", 
                  borderTopColor: "transparent", 
                  borderRadius: "50%", 
                  animation: "spinner 1s linear infinite",
                  display: syncProgress === 100 ? "none" : "block"
                }} />
                <span>
                  {syncProgress < 100 
                    ? `Syncing interview video... ${syncProgress}%` 
                    : "✅ Video synced successfully! Safe to close this page."}
                </span>
              </div>
            )}

            {/* Sync Fallbacks: Retry and Download */}
            {(isSyncError || (isSyncingVideo && syncProgress < 5)) && (
              <div className="sync-fallbacks" style={{ margin: "10px 0", display: "flex", gap: "10px", justifyContent: "center" }}>
                <button 
                  onClick={() => handleSaveInterview()}
                  className="retry-sync-btn"
                  style={{ background: "#4f46e5", color: "white", border: "none", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", cursor: "pointer" }}
                >
                  🔄 Retry Sync
                </button>
                {finalVideoBlob && (
                  <button 
                    onClick={() => {
                        const url = URL.createObjectURL(finalVideoBlob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `interview_${Date.now()}.webm`;
                        a.click();
                    }}
                    className="download-local-btn"
                    style={{ background: "#10b981", color: "white", border: "none", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", cursor: "pointer" }}
                  >
                    ⬇️ Download Recording (Backup)
                  </button>
                )}
              </div>
            )}
            {isSyncingVideo && syncProgress < 100 && (
              <div className="sync-progress-bar" style={{ 
                width: "100%", 
                height: 4, 
                background: "#e2e8f0", 
                borderRadius: 2, 
                marginTop: -8, 
                marginBottom: 20,
                overflow: "hidden"
              }}>
                <div className="sync-progress-fill" style={{ 
                  width: `${syncProgress}%`, 
                  height: "100%", 
                  background: "#0ea5e9",
                  transition: "width 0.3s ease"
                }} />
              </div>
            )}
            {cheatingFlagsRef.current > 0 && (
              <div className="cheat-banner">
                ⚠️ {cheatingFlagsRef.current} integrity violation{cheatingFlagsRef.current > 1 ? "s" : ""} flagged during interview
              </div>
            )}
          </div>

          <div className="score-overview">
            <div className="score-overview-card">
              <div className="score-ring" style={{ borderColor: getScoreColor(resumeData?.resumeScore || 0) }}>
                <span className="ring-value">{resumeData?.resumeScore || 0}</span>
                <span className="ring-label">/10</span>
              </div>
              <h4>Resume Score</h4>
              <span className="score-badge" style={{ backgroundColor: getScoreColor(resumeData?.resumeScore || 0) }}>
                {resumeData?.resumeUrl ? "Ready" : "Optimizing..."}
              </span>
            </div>
            <div className="score-overview-card">
              <div className="score-ring" style={{ borderColor: getScoreColor(interviewScore) }}>
                <span className="ring-value">{interviewScore}</span>
                <span className="ring-label">/10</span>
              </div>
              <h4>Interview Score</h4>
              <span className="score-badge" style={{ backgroundColor: getScoreColor(interviewScore) }}>
                {interviewScore >= 7 ? "Good" : interviewScore >= 4 ? "Average" : "Improve"}
              </span>
            </div>
            <div className="score-overview-card final">
              <div className="score-ring large" style={{ borderColor: getScoreColor(finalScore) }}>
                <span className="ring-value">{finalScore}</span>
                <span className="ring-label">/10</span>
              </div>
              <h4>Final Score</h4>
              <span className="score-formula">Resume 40% + Interview 60%</span>
            </div>
          </div>

          {!excelDataFinal && (
            <div className="result-section" style={{ textAlign: "center", backgroundColor: "#1e293b", padding: "30px", borderRadius: "16px", border: "1px dashed #3b82f6" }}>
              <h3 style={{ margin: "0 0 10px 0" }}>📊 Optional: Practical Excel Test</h3>
              <p style={{ color: "#94a3b8", marginBottom: "20px" }}>
                Showcase your spreadsheet skills in a simulated environment. This test is completely optional but may boost your profile!
              </p>
              <button 
                onClick={() => setIsExcelPhase(true)}
                style={{
                  padding: "12px 24px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.2), 0 2px 4px -1px rgba(16, 185, 129, 0.1)"
                }}
              >
                Start Excel Assessment
              </button>
            </div>
          )}

          {/* Resume AI Analysis */}
          {(resumeData?.strengths?.length > 0 || resumeData?.weaknesses?.length > 0) && (
            <div className="result-section">
              <h3>📄 AI Resume Analysis</h3>
              <div className="ai-analysis-grid">
                {resumeData.strengths?.length > 0 && (
                  <div className="ai-analysis-card strengths">
                    <h4>✅ Strengths</h4>
                    <ul>{resumeData.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </div>
                )}
                {resumeData.weaknesses?.length > 0 && (
                  <div className="ai-analysis-card weaknesses">
                    <h4>⚠️ Areas to Improve</h4>
                    <ul>{resumeData.weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </div>
                )}
              </div>
              {resumeData.detectedSkills?.length > 0 && (
                <div className="found-skills" style={{ marginTop: 16 }}>
                  <span className="skills-label">Detected Skills:</span>
                  <div className="skills-tags">
                    {resumeData.detectedSkills.slice(0, 15).map(s => (
                      <span key={s} className="skill-tag">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Answer Feedback */}
          <div className="result-section">
            <h3>💬 Interview Answers</h3>
            <div className="answers-list">
              {answers.map((a, idx) => (
                <div key={idx} className="answer-card">
                  <div className="answer-header">
                    <span className="q-number">Q{idx + 1}</span>
                    <span className="q-score" style={{ color: getScoreColor(a.score) }}>{a.score}/10</span>
                  </div>
                  <p className="q-text">{a.question || a.Question}</p>
                  <p className="a-text">{a.answer}</p>
                  {a.feedback && <p className="a-feedback">🤖 AI Feedback: {a.feedback}</p>}
                </div>
              ))}
            </div>
          </div>

          <div className="result-section">
            <h3>🎥 Video Recording</h3>
            <video src={savedInterview.videoUrl} controls className="result-video" />
          </div>

          <div style={{ textAlign: "center", marginTop: "40px", paddingBottom: "20px" }}>
            <button
              onClick={async () => {
                // Don't let the candidate leave while the recording is still uploading —
                // logging out aborts the background upload and the video never syncs.
                if (isSyncingVideo || isSyncError) {
                  const ok = window.confirm(
                    "⚠️ Your interview video is still uploading.\n\n" +
                    "If you log out now, the recording will NOT be saved to the dashboard. " +
                    "Please wait until you see \"Video synced successfully\".\n\nLog out anyway?"
                  );
                  if (!ok) return;
                }
                const keys = [
                  "iv_candidateName", "iv_isFieldSelecting", "iv_selectedField", "iv_questions",
                  "iv_currentIndex", "iv_isInterviewStarted", "iv_resumeData", "iv_isFormSubmitted",
                  "iv_answers", "iv_currentInterviewId", "iv_selectedPosition"
                ];
                keys.forEach(k => localStorage.removeItem(k));
                try {
                  await signOut(auth);
                  navigate("/");
                } catch (error) {
                  console.error("Logout failed", error);
                  toast.error("Failed to log out.");
                }
              }}
              style={{
                padding: "12px 30px",
                fontSize: "1.05rem",
                fontWeight: "bold",
                backgroundColor: "#ef4444",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                boxShadow: "0 4px 6px -1px rgba(239, 68, 68, 0.2), 0 2px 4px -1px rgba(239, 68, 68, 0.1)",
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#dc2626"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#ef4444"}
            >
              🚪 Log Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Interview UI ───
  return (
    <div className="interview-page">
      <Toaster position="top-right" toastOptions={{ style: { fontFamily: "Inter, sans-serif", fontSize: 14 } }} />

      <div className="interview-wrapper">
        {/* Field Selection Modal */}
        {isFieldSelecting && (
          <div className="field-selector-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.95)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="field-selector-card" style={{
              background: 'white', padding: '40px', borderRadius: '24px',
              maxWidth: '500px', width: '90%', textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <h2 style={{ fontSize: '28px', color: '#1e293b', marginBottom: '16px' }}>Technical Phase</h2>
              <p style={{ color: '#64748b', marginBottom: '32px' }}>
                You've completed the general and aptitude rounds. Please select your specialized field for the technical round.
              </p>
              <div className="field-buttons" style={{ display: 'grid', gap: '16px', maxHeight: '400px', overflowY: 'auto', padding: '10px' }}>
                {positions.length === 0 ? (
                  <p style={{ color: "#64748b" }}>No technical positions available. Please contact HR.</p>
                ) : (
                  positions.map((pos) => (
                    <button 
                      key={pos.id}
                      onClick={() => handleFieldSelect(pos.title)}
                      style={{
                        padding: '20px', borderRadius: '16px', border: '2px solid #e2e8f0',
                        background: 'white', color: '#1e293b', fontSize: '18px', fontWeight: '600',
                        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#f8faff'; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                    >
                      💼 {pos.title}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {role === "admin" ? (
          <div className="admin-header" style={{ marginBottom: 24 }}>
            <div className="admin-header-left">
              <h2 className="interview-title" style={{ margin: 0, textAlign: "left" }}>
                <span className="title-icon">🎯</span>
                AI Hiring Platform — Interview Test
              </h2>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "14px" }}>Test the interview process as a candidate.</p>
            </div>
            <button className="back-btn" onClick={() => navigate("/dashboard")}>
              ← Back to Dashboard
            </button>
          </div>
        ) : (
          <h2 className="interview-title">
            <span className="title-icon">🎯</span>
            AI Hiring Platform — Interview
          </h2>
        )}

        {/* Position Selection Gate */}
        {isUploading && (
          <div className="submitting-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.8)', zIndex: 10000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(5px)', color: 'white'
          }}>
            <div className="spinner" style={{ marginBottom: '20px', width: '50px', height: '50px', border: '5px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spinner 1s linear infinite' }}></div>
            <h2 style={{ margin: 0 }}>Submitting Interview...</h2>
            <p style={{ opacity: 0.8, marginTop: '8px' }}>Finalizing results and preparing your report.</p>
          </div>
        )}

        {!selectedPosition ? (
          <div className="pre-interview">
            <div className="step-card" style={{ textAlign: "center" }}>
              <div className="step-badge">Select Position</div>
              <h3 style={{ fontSize: "1.3rem", color: "#1e293b", marginBottom: 8 }}>💼 Choose Your Target Position</h3>
              <p style={{ color: "#64748b", fontSize: "0.9rem", marginBottom: 24 }}>
                Select the position you are applying for. The AI will evaluate your resume and interview answers based on the role's requirements.
              </p>
              {positions.length === 0 ? (
                <div style={{ padding: 30, color: "#94a3b8" }}>
                  <span style={{ fontSize: 40 }}>📋</span>
                  <p style={{ marginTop: 8 }}>No positions available. Please ask the admin to add positions.</p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 14, maxWidth: 500, margin: "0 auto" }}>
                  {positions.map((pos) => (
                    <button
                      key={pos.id}
                      onClick={() => setSelectedPosition(pos)}
                      style={{
                        padding: "18px 20px",
                        borderRadius: 14,
                        border: "2px solid #e2e8f0",
                        background: "white",
                        cursor: "pointer",
                        transition: "all 0.25s",
                        textAlign: "left",
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.background = "#f5f3ff"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(99,102,241,0.15)"; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "white"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", marginBottom: 6 }}>
                        💼 {pos.title}
                      </div>
                      <div style={{
                        fontSize: "0.8rem",
                        color: "#64748b",
                        lineHeight: 1.4,
                        maxHeight: 60,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {pos.jobDescription?.length > 150 ? pos.jobDescription.slice(0, 150) + "..." : pos.jobDescription}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : !isInterviewStarted ? (
          <div className="pre-interview">
            <div className="step-card">
              <div className="step-badge" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
                <span>Step 1 — {selectedPosition.title.toUpperCase()}</span>
                <button 
                  onClick={() => { setSelectedPosition(null); localStorage.removeItem("iv_selectedPosition"); }}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', padding: 0 }}
                >
                  Change Position
                </button>
              </div>
              <ResumeUpload
                userId={user?.uid || user?.email || "anonymous"}
                jobDescription={selectedPosition?.jobDescription || ""}
                onResumeProcessed={async (data) => {
                  setResumeData(data);
                  
                  // --- INSTANT PERSISTENCE ---
                  const userId = user?.uid || user?.email || "anonymous";
                  const interviewPayload = {
                    "User ID": userId,
                    "Resume FileName": data.resumeFileName || "",
                    "Resume URL": data.resumeUrl || "",
                    "Resume Score": data.resumeScore || 0,
                    "Resume Feedback": data.resumeFeedback || "N/A",
                    "Resume Details": JSON.stringify({
                      foundSkills: data.detectedSkills || [],
                      foundSections: data.foundSections || [],
                      wordCount: data.wordCount || 0,
                      lengthScore: data.lengthScore || 0,
                      keywordScore: data.keywordScore || 0,
                      sectionScore: data.sectionScore || 0
                    }),
                    "Resume Strengths": JSON.stringify(data.strengths || []),
                    "Resume Weaknesses": JSON.stringify(data.weaknesses || []),
                    "Position Title": selectedPosition?.title || "",
                    "Status": data.resumeUrl ? "ready" : "uploading",
                    "Started At": Date.now(),
                  };

                  try {
                    if (!currentInterviewIdRef.current) {
                      // Use createRecord directly to ensure all fields are passed
                      const recordId = await createRecord(LARK_TABLES.INTERVIEWS, interviewPayload);
                      setCurrentInterviewId(recordId);
                      currentInterviewIdRef.current = recordId;
                      console.log("Initial interview record created with full AI data:", recordId);
                    } else {
                      await updateInterview(currentInterviewIdRef.current, interviewPayload);
                      console.log("Interview record updated with AI analysis & URL:", currentInterviewIdRef.current);
                    }
                  } catch (err) {
                    console.error("Error persisting AI resume data:", err);
                  }

                  if (data.resumeUrl) {
                    toast.success("✅ Resume finalized in database!", { duration: 3000 });
                  }
                }}
              />
            </div>
            <div className="step-card">
              <div className="step-badge">Step 2</div>
              <div className="start-interview-section">
                <h3>Candidate Information</h3>
                <p style={{ color: "#64748b", fontSize: 14 }}>
                  Please fill out the professional information form to proceed.
                </p>
                {isFormSubmitted ? (
                  <div className="form-status-msg">
                    <span>✅ Information Form Submitted Successfully!</span>
                  </div>
                ) : (
                  <button 
                    className="fill-form-btn" 
                    onClick={() => setIsFormOpen(true)}
                  >
                    📝 Fill Information Form
                  </button>
                )}
              </div>
            </div>

            {/* Form Modal Overlay */}
            {isFormOpen && (
              <div className="form-overlay">
                <div className="form-modal">
                  <div className="form-modal-header">
                    <h2>Employee/Intern Information Form</h2>
                    <button className="close-form-btn" onClick={() => setIsFormOpen(false)}>×</button>
                  </div>
                  <div className="form-modal-content">
                    <EmployeeForm 
                      userId={user?.uid || user?.email || "anonymous"} 
                      onFormSubmitted={async (name) => {
                        setCandidateName(name);
                        setIsFormSubmitted(true);
                        setIsFormOpen(false);
                        
                        // Update existing interview record with name if it exists
                        if (currentInterviewIdRef.current) {
                          try {
                            await updateInterview(currentInterviewIdRef.current, { 
                              "Candidate Name": name 
                            });
                          } catch (err) {
                            console.error("Error updating candidate name:", err);
                          }
                        }
                      }} 
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={`step-card ${!isFormSubmitted ? "disabled" : ""}`}>
              <div className="step-badge">Step 3</div>
              <div className="start-interview-section">
                <h3>Start Your Interview</h3>
                <div className="interview-info-grid">
                  <div className="info-badge">📋 {questions.length} Questions</div>
                  <div className="info-badge">🤖 AI Evaluation</div>
                  <div className="info-badge">👁️ Face Detection</div>
                </div>
                <p style={{ color: "#64748b", fontSize: 14, margin: "12px 0" }}>
                  Your camera, microphone, and screen activity will be monitored.
                </p>
                <button
                  onClick={handleStartInterview}
                  disabled={!isFormSubmitted}
                  className="start-interview-btn"
                >
                  {!resumeData 
                    ? "Upload Resume First" 
                    : !isFormSubmitted 
                      ? "Submit Info Form First" 
                      : "🎬 Start Interview"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="active-interview">
            {/* Warning banners */}
            {tabWarning && (
              <div className="warning-banner tab-warning">
                🚨 Tab switch detected! This violation has been recorded. ({cheatingFlags} total)
              </div>
            )}
            {faceWarning && (
              <div className={`warning-banner ${faceStatus === "multiple" ? "face-warning-red" : "face-warning-yellow"}`}>
                {faceWarning}
              </div>
            )}

            {/* Progress */}
            <div className="interview-progress">
              <div className="progress-label">Question {currentIndex + 1} of {questions.length}</div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }} />
              </div>
            </div>

            <div className="interview-layout">
              {/* Video panel */}
              <div className="video-panel">
                <video ref={videoRef} autoPlay playsInline muted className="interview-video" />
                <div className="recording-indicator"><span className="rec-dot" />REC</div>
                {faceStatus !== "ok" && faceStatus !== "idle" && (
                  <div className={`face-status-badge ${faceStatus}`}>
                    {faceStatus === "no-face" ? "⚠️ Look at camera" : "🚨 Multiple faces"}
                  </div>
                )}
                {/* Cheating counter */}
                {cheatingFlags > 0 && (
                  <div className="cheat-counter">🚩 {cheatingFlags} flag{cheatingFlags > 1 ? "s" : ""}</div>
                )}
              </div>

              {/* Question panel */}
              <div className="question-panel">

                {/* Question */}
                <div className="question-card-interview">
                  <div className="question-number">Question {currentIndex + 1} of {questions.length}</div>
                  <p className="question-text-content">{questions[currentIndex]?.question || questions[currentIndex]?.Question || "Loading question..."}</p>
                  {(questions[currentIndex].category || questions[currentIndex].Category) && (
                    <span className="question-tag">{questions[currentIndex].category || questions[currentIndex].Category}</span>
                  )}
                  {(questions[currentIndex].difficulty || questions[currentIndex].Difficulty) && (
                    <span className="question-tag diff">{questions[currentIndex].difficulty || questions[currentIndex].Difficulty}</span>
                  )}
                </div>

                {/* Speech */}
                <div className="speech-section" style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
                  <div className="speech-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span className={`mic-indicator ${isMicOffline ? "offline" : isListening ? "active" : ""}`}>
                      {isMicOffline ? "❌ Mic Blocked" : isListening ? "🎤 Listening..." : "🎤 Mic Off"}
                    </span>
                    {isMicOffline && (
                      <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: "bold", background: "#fef2f2", padding: "2px 8px", borderRadius: "4px", border: "1px solid #fee2e2" }}>
                        Hardware blocked. Please type your answer below.
                      </span>
                    )}
                    <div style={{ display: "flex", gap: "8px" }}>
                      <select 
                        value={spokenLanguage} 
                        onChange={e => setSpokenLanguage(e.target.value)} 
                        disabled={isListening}
                        className="lang-select"
                        style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #cbd5e1", outline: "none" }}
                      >
                        <option value="en-US">English</option>
                        <option value="hi-IN">Hindi</option>
                        <option value="gu-IN">Gujarati</option>
                      </select>
                      {!isListening
                        ? <button className="mic-btn" onClick={startListening}>Start Mic</button>
                        : <button className="mic-btn stop" onClick={stopListening}>Stop Mic</button>
                      }
                    </div>
                  </div>

                  <div className="answer-display" style={{ minHeight: "50px", background: "#f8fafc", padding: "10px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px", fontWeight: "600" }}>
                      Live Microphone Transcript (Read-only):
                    </div>
                    <div style={{ color: "#94a3b8", fontStyle: "italic" }}>
                      {currentAnswer || "Speak now..."}
                    </div>
                  </div>
                </div>

                <div className="translated-section" style={{ position: "relative", marginBottom: "16px" }}>
                   <div style={{ fontSize: "12px", color: "#1e293b", marginBottom: "4px", paddingLeft: "4px", fontWeight: "700" }}>
                     Your Final Answer (Edit if needed): {isTranslating && <span style={{ color: "#0ea5e9" }}>🔄 Translating...</span>}
                   </div>
                    <textarea
                      className="manual-answer"
                      placeholder={isMicOffline ? "Type your answer here..." : "Your spoken answer will appear here. You can edit it before clicking Next..."}
                      value={isEditingAnswer || isMicOffline ? editedAnswer : (translatedAnswer || currentAnswer)}
                      onChange={(e) => {
                        setEditedAnswer(e.target.value);
                        setIsEditingAnswer(true);
                        // If they start typing, stop the mic to prevent interference
                        if (isListening) stopListening();
                      }}
                      rows={4}
                      disabled={isTranslating}
                      style={{ 
                        width: "100%", 
                        padding: "12px", 
                        borderRadius: "8px", 
                        border: isEditingAnswer ? "2px solid #0ea5e9" : "1px solid #cbd5e1",
                        background: isEditingAnswer ? "#f0f9ff" : "white",
                        fontSize: "15px",
                        outline: "none"
                      }}
                    />
                </div>

                <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                  <button
                    onClick={handleSkipQuestion}
                    disabled={isUploading || isEvaluating}
                    className="skip-question-btn"
                    style={{ 
                      flex: 1,
                      padding: "12px", 
                      borderRadius: "8px", 
                      background: "#f1f5f9", 
                      color: "#475569", 
                      border: "1px solid #e2e8f0",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    Skip Question ⏭️
                  </button>
                   <button
                    onClick={handleNextQuestion}
                    disabled={isUploading || isEvaluating}
                    className="next-question-btn"
                    style={{ 
                      flex: 2,
                      padding: "12px", 
                      borderRadius: "8px", 
                      background: "linear-gradient(135deg, #2563eb, #1d4ed8)", 
                      color: "white", 
                      border: "none",
                      fontWeight: "700",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      cursor: "pointer"
                    }}
                  >
                    {isEvaluating ? "🤖 Evaluating..." : "Submit Answer & Next 🚀"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidateInterview;